import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { savePassage } from "@/lib/passageCache";
import useIsDesktop from "@/hooks/useIsDesktop";
import StructuredStudy from "@/components/StructuredStudy";
import DeeperChat from "@/components/DeeperChat";
import StudyTools from "@/components/StudyTools";
import ShareStudyDialog from "@/components/ShareStudyDialog";
import DesktopStudyWorkspace from "@/components/DesktopStudyWorkspace";
import { ChevronLeft, Loader2, Sparkles, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";

export default function StudySession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [alone, setAlone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const POLL_DEADLINE_MS = 180000; // give even a slow Sonnet study room to finish

    const load = async (isPoll) => {
      try {
        const [{ data }, settings] = await Promise.all([
          api.get(`/study/sessions/${sessionId}`),
          isPoll ? Promise.resolve({ data: null }) : api.get("/settings").catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;

        // Apply the saved study_mode from the first (non-poll) load, even while the
        // study is still generating, so an "alone" preference isn't lost on new sessions.
        if (settings.data) setAlone((settings.data?.study_mode || "companion") === "alone");

        if (data.status === "error") {
          toast.error(data.error || "Couldn't generate the study. Please try again.");
          navigate("/study");
          return;
        }

        const ready = !!data.study || data.status === "ready";
        if (!ready && data.status === "generating") {
          if (Date.now() - startedAt > POLL_DEADLINE_MS) {
            toast.error("This study is taking longer than expected. Please try again.");
            navigate("/study");
            return;
          }
          setGenerating(true);
          setLoading(false);
          setPreview(data);
          // Poll quickly until the early Scripture preview lands, then relax the cadence.
          timer = setTimeout(() => load(true), data.passage ? 2000 : 500);
          return;
        }

        setSession(data);
        setGenerating(false);
        // Cache raw passage text so it re-opens offline (item 6).
        if (data.study?.passage_reference && data.passage) {
          savePassage(data.study.passage_reference, data.passage.translation, data.passage);
        }
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load session");
        navigate("/study");
      }
    };

    setLoading(true);
    load(false);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, navigate]);

  if (loading || !session) {
    const pv = preview?.passage;
    const verses = pv?.verses || [];
    return (
      <div className="min-h-[60vh] px-6 py-8 max-w-2xl mx-auto">
        {verses.length > 0 ? (
          <div className="animate-in fade-in duration-500" data-testid="study-passage-preview">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500 mb-3">
              <span className="text-[#00E5FF]">{preview?.input}</span>
              {pv?.translation_name && (<><span>·</span><span>{pv.translation_name}</span></>)}
            </div>
            <div className="text-xl leading-relaxed text-slate-200 space-y-1">
              {verses.map((v, i) => (
                <span key={i}>
                  <sup className="text-[0.6em] text-[#00E5FF] mr-0.5 align-super">{v.verse}</sup>
                  {v.text}{" "}
                </span>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 text-[#00E5FF] animate-spin shrink-0" strokeWidth={1.5} />
              <span data-testid="study-generating">Preparing your study — sitting with the text for a moment.</span>
            </div>
            <div className="mt-6 space-y-3" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-3.5 w-1/3 rounded bg-[#1E273D]/80 animate-pulse" />
                  <div className="h-3 w-full rounded bg-[#141B29] animate-pulse" />
                  <div className="h-3 w-11/12 rounded bg-[#141B29] animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-center">
            <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
            {generating && (
              <p
                data-testid="study-generating"
                className="text-sm text-slate-400 max-w-xs leading-relaxed animate-in fade-in duration-500"
              >
                Preparing your study — sitting with the text for a moment. This can take up to a minute.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  if (isDesktop) {
    return (
      <DesktopStudyWorkspace
        session={session}
        setSession={setSession}
      />
    );
  }

  return (
    <div className="px-6 py-6 pb-44">
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="back-to-study"
          onClick={() => navigate("/study")}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> New study
        </button>
        <ShareStudyDialog sessionId={session.id} title={session.study?.title} testid="share-study" />
      </div>

      {/* Companion / Alone toggle */}
      <div className="flex gap-2 p-1 bg-[#141B29] rounded-xl mb-8">
        <button
          data-testid="mode-companion"
          onClick={() => setAlone(false)}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            !alone ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
          }`}
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.5} /> With virtual study companion
        </button>
        <button
          data-testid="mode-alone"
          onClick={() => setAlone(true)}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            alone ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
          }`}
        >
          <BookOpenCheck className="w-4 h-4" strokeWidth={1.5} /> Study alone
        </button>
      </div>

      {/* The full study renders immediately — no gate. */}
      <StructuredStudy
        session={session}
        onPassageChange={(p) => setSession((s) => ({ ...s, passage: p }))}
        onStudyReload={(data) => setSession(data)}
        showGenerators
      />

      {alone && (
        <div className="mt-10 pt-8 border-t border-[#1E273D]">
          <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-5">Study tools</div>
          <StudyTools session={session} onNotesChange={(t) => setSession((s) => ({ ...s, notes: t }))} />
        </div>
      )}

      {/* Go Deeper is ALWAYS available in a session — companion mode or study-alone */}
      <DeeperChat sessionId={session.id} initialChat={session.chat || []} initialHighlights={session.highlights || []} />
    </div>
  );
}
